// src/pages/tests/TestPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Stack,
  Typography,
  LinearProgress,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Backdrop,
  TextField,
  Alert,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { supabase, markingDb } from '../../supabaseClient.ts';
import RichText from '../../components/RichText.tsx';
import QuestionActionButtons from '../../components/QuestionActionButtons.tsx';
import { ensureInlineMath } from '../../utils/latex.ts';
import {
  clientScoresMarkingType,
  isAiMarkingType,
  resolveMarkingType,
} from '../../utils/markingType.ts';
import { scoreQuestionSubmission, toSubmittableQuestion } from '../../utils/markSubmission.ts';
import { fetchTestQuestionRows, queryQuestionsWithMarkingFallback, REVIEW_QUESTION_SELECT } from '../../utils/questionSelect.ts';
import { invokeShadowMarkTestAttempt, invokeShadowMarkAiSubmission } from '../../utils/shadowMark.ts';
import { useEffectiveStudentId } from '../../hooks/useEffectiveStudentId.ts';
import { useTutorSession } from '../../context/TutorSessionContext.tsx';
import { logTutorSessionEvent } from '../../utils/tutorSessionEvents.ts';

import QuestionMC from './questions/MultiChoice.tsx';
import QuestionInput from './questions/Input.tsx';
import { choicesAsFlatStrings, getTableChoiceData, unwrapChoicesJson } from '../../utils/questionChoices.ts';
import { McChoiceTableStudent } from '../../components/McChoiceTableStudent.tsx';

/* ------------------------------------------------------------------
   Types
-------------------------------------------------------------------*/
export type Question = {
  id: string;
  prompt: string;
  type: 'mc' | 'input';
  marks: number;
  choices: any;               // can be array/object in DB; we’ll normalise
  answer_index: number | null;
  answer: string | null;
  worked_solution?: string | null;
  ordinal?: number;
  unit?: string | null;
  marking_method?: import('../../types/markingCriteria').MarkingMethod;
  deterministic_config?: import('../../types/markingCriteria').DeterministicConfig;
  ai_config?: import('../../types/markingCriteria').AiConfig;

  // Multi-part extras (populated at load time for all parts in the group)
  group_id?: string | null;
  group_prompt?: string | null;
  part_label?: string | null;   // "Part A", "Part B", ...
};

interface QuestionResult {
  questionId: string;
  type: 'mc' | 'input';
  prompt: string;
  /** Flattened option text (list MC, or table rows as "a | b | c") for storage / fallbacks */
  choices?: string[];
  /** Original `questions.choices` JSON when table layout — for review UI */
  mcChoicesRaw?: unknown;
  correctIndex?: number;
  selectedIndex?: number;
  yourAnswer: string;
  correctAnswer: string;
  workedSolution?: string | null;
  score: number;
  maxMarks: number;
  timeTaken: number;
  markingStatus: 'auto_marked' | 'pending_manual' | 'manually_marked';
  tutorAwardedMarks?: number | null;
  tutorFeedback?: string | null;
  responseFormat?: 'mathlive' | 'richtext';
  selfMarked?: boolean;
  /** Short feedback from the AI marker when it re-marked a deterministically-wrong answer. */
  aiFeedback?: string | null;
  /** True when the AI marker adjusted this result after the deterministic engine. */
  aiAdjudicated?: boolean;
}

/* helpers */
const asChoiceArray = (choices: any): string[] => choicesAsFlatStrings(choices);
const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim();

/* ── sessionStorage helpers for test progress persistence ── */
const PROGRESS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface StashedAnswer {
  selected?: number;
  typed: string;
}

interface SavedProgress {
  step: number;
  results: QuestionResult[];
  score: number;
  selected?: number;
  typed: string;
  questionCount: number;
  savedAt: number;
  stashedAnswers?: Record<number, StashedAnswer>;
}

const progressKey = (testId: string) => `test-progress-${testId}`;

const saveProgress = (
  testId: string,
  state: Omit<SavedProgress, 'savedAt'>,
) => {
  try {
    const payload: SavedProgress = { ...state, savedAt: Date.now() };
    sessionStorage.setItem(progressKey(testId), JSON.stringify(payload));
  } catch { /* quota exceeded or private mode — silently skip */ }
};

const loadProgress = (
  testId: string,
  questionCount: number,
): SavedProgress | null => {
  try {
    const raw = sessionStorage.getItem(progressKey(testId));
    if (!raw) return null;
    const parsed: SavedProgress = JSON.parse(raw);
    if (Date.now() - parsed.savedAt > PROGRESS_TTL_MS) {
      sessionStorage.removeItem(progressKey(testId));
      return null;
    }
    if (parsed.questionCount !== questionCount) {
      sessionStorage.removeItem(progressKey(testId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const clearProgress = (testId: string) => {
  try { sessionStorage.removeItem(progressKey(testId)); } catch { /* noop */ }
};

/* ------------------------------------------------------------------
   Component
-------------------------------------------------------------------*/
const TestPage: React.FC = () => {
  const { studentId } = useEffectiveStudentId();
  const { tutorSession } = useTutorSession();
  const { testId, resultId } = useParams<{ testId: string; resultId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMarkingRoute = location.pathname.endsWith('/mark');

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<number>();
  const [typed, setTyped] = useState('');
  const [score, setScore] = useState(0);
  const [results, setResults] = useState<QuestionResult[]>([]);

  // phases: doing → (submit) → review
  const [phase, setPhase] = useState<'doing' | 'review'>('doing');
  const [submitting, setSubmitting] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const startRef = useRef<number>(Date.now());
  const stashedAnswersRef = useRef<Record<number, StashedAnswer>>({});
  const q = questions[step];

  // Cheat sheet state
  const [showCheatSheet, setShowCheatSheet] = useState(false);
  const [cheatSheetEquations, setCheatSheetEquations] = useState<Array<{ id: string; category_id: string; category_name: string; equation: string }>>([]);
  const [showCheatSheetDialog, setShowCheatSheetDialog] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [resultMarkingStatus, setResultMarkingStatus] = useState<'final' | 'pending_manual'>('final');
  const [markDrafts, setMarkDrafts] = useState<Record<string, string>>({});
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<string, string>>({});
  const [savingMarks, setSavingMarks] = useState(false);
  const [markingError, setMarkingError] = useState<string | null>(null);
  const [markingMessage, setMarkingMessage] = useState<string | null>(null);
  const [hasMarkingColumns, setHasMarkingColumns] = useState(true);
  const [savingSelfMarks, setSavingSelfMarks] = useState(false);
  const [submittedResultId, setSubmittedResultId] = useState<string | null>(null);

  const isTutorMarkingMode = isMarkingRoute && !!tutorSession?.id;
  const isSessionSelfMarkMode = !!tutorSession?.id && !isTutorMarkingMode;
  const activeResultId = resultId ?? submittedResultId;
  const isManualInputQuestion = (question: Question) =>
    question.type === 'input' && resolveMarkingType(question) === 'manual';

  useEffect(() => {
    if (!studentId) {
      setCompanyId(null);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('students')
        .select('company_id')
        .eq('id', studentId)
        .maybeSingle();
      if (!error) {
        setCompanyId(data?.company_id ?? null);
      }
    })();
  }, [studentId]);

  /* ========== MODE A: viewing a past attempt ========== */
  useEffect(() => {
    if (!testId || !resultId) return;
    (async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated.');

        if (!studentId) {
          throw new Error('Student record not found.');
        }

        let resRow: any = null;
        let resErr: any = null;
        const withMarkingResult = await markingDb
          .from('test_results')
          .select('id, test_id, student_id, score, total_marks, marking_status')
          .eq('id', resultId)
          .single();
        resRow = withMarkingResult.data;
        resErr = withMarkingResult.error;
        if (
          resErr &&
          typeof resErr.message === 'string' &&
          (resErr.message.includes('marking_status') || resErr.message.includes('schema cache'))
        ) {
          const legacyResult = await markingDb
            .from('test_results')
            .select('id, test_id, student_id, score, total_marks')
            .eq('id', resultId)
            .single();
          resRow = legacyResult.data;
          resErr = legacyResult.error;
          if (!resErr) setHasMarkingColumns(false);
        } else if (!resErr) {
          setHasMarkingColumns(true);
        }
        if (resErr) throw new Error(resErr.message);
        if (!resRow || resRow.test_id !== testId || resRow.student_id !== studentId) {
          throw new Error('Result not found.');
        }

        let qrs: any[] | null = null;
        let qrsErr: any = null;
        const withMarkingRows = await markingDb
          .from('question_results')
          .select('question_id, earned_marks, time_taken, your_answer, selected_index, marking_status, tutor_awarded_marks, tutor_feedback')
          .eq('result_id', resultId);
        qrs = withMarkingRows.data;
        qrsErr = withMarkingRows.error;
        if (
          qrsErr &&
          typeof qrsErr.message === 'string' &&
          (qrsErr.message.includes('marking_status') ||
            qrsErr.message.includes('tutor_awarded_marks') ||
            qrsErr.message.includes('schema cache'))
        ) {
          const legacyRows = await markingDb
            .from('question_results')
            .select('question_id, earned_marks, time_taken, your_answer, selected_index')
            .eq('result_id', resultId);
          qrs = legacyRows.data;
          qrsErr = legacyRows.error;
          if (!qrsErr) setHasMarkingColumns(false);
        } else if (!qrsErr) {
          setHasMarkingColumns(true);
        }
        if (qrsErr) throw new Error(qrsErr.message);

        const qIds = (qrs ?? []).map((r) => r.question_id);
        const { data: qs, error: qErr } = await queryQuestionsWithMarkingFallback(
          (select) => supabase.from('questions').select(select).in('id', qIds),
          REVIEW_QUESTION_SELECT,
        );
        if (qErr) throw new Error(qErr.message);

        const byQ: Record<string, Question> = {};
        (qs ?? []).forEach((q) => (byQ[q.id] = q as Question));

        const built: QuestionResult[] = (qrs ?? []).map((r: any) => {
          const q = byQ[r.question_id];
          const maxMarks = Number(q?.marks ?? 0);
          const earned = Number(r.earned_marks ?? 0);
          const tt = String(r.time_taken ?? '');
          const secs = parseInt(tt.replace(/[^\d]/g, ''), 10) || 0;

          const choices = q ? asChoiceArray(q.choices) : [];
          const selIdx: number | undefined =
            typeof r.selected_index === 'number' ? r.selected_index : undefined;

          let yourAnswer = String(r.your_answer ?? '');
          if (!yourAnswer && q?.type === 'mc' && selIdx != null) {
            yourAnswer = choices[selIdx] ?? '';
          }

          return {
            questionId: r.question_id,
            type: q?.type ?? 'input',
            prompt: q?.prompt ?? '',
            choices: q?.type === 'mc' ? choices : undefined,
            mcChoicesRaw: q?.type === 'mc' ? unwrapChoicesJson(q.choices) : undefined,
            correctIndex: q?.type === 'mc' ? (q?.answer_index ?? undefined) : undefined,
            selectedIndex: q?.type === 'mc' ? selIdx : undefined,
            yourAnswer,
            correctAnswer:
              q?.type === 'mc'
                ? (q && q.answer_index != null ? choices[q.answer_index] ?? '' : '')
                : (q?.answer ?? ''),
            workedSolution: q?.worked_solution ?? null,
            score: earned,
            maxMarks,
            timeTaken: secs,
            markingStatus: (r.marking_status ?? 'auto_marked') as 'auto_marked' | 'pending_manual' | 'manually_marked',
            tutorAwardedMarks: r.tutor_awarded_marks ?? null,
            tutorFeedback: r.tutor_feedback ?? null,
            responseFormat: q && resolveMarkingType(q) === 'manual' ? 'richtext' : 'mathlive',
            selfMarked: false,
          };
        });

        setResults(built);
        setScore(Number(resRow.score) || 0);
        setResultMarkingStatus((resRow.marking_status ?? 'final') as 'final' | 'pending_manual');
        const nextMarks: Record<string, string> = {};
        const nextFeedback: Record<string, string> = {};
        built.forEach((row) => {
          if (row.markingStatus !== 'auto_marked') {
            const seed = row.tutorAwardedMarks ?? (row.markingStatus === 'manually_marked' ? row.score : null);
            nextMarks[row.questionId] = seed == null ? '' : String(seed);
            nextFeedback[row.questionId] = row.tutorFeedback ?? '';
          }
        });
        setMarkDrafts(nextMarks);
        setFeedbackDrafts(nextFeedback);
        setPhase('review');
        setStep(built.length);
        setError(null);
      } catch (e: any) {
        setError(e.message || 'Failed to load attempt.');
      } finally {
        setLoading(false);
      }
    })();
  }, [testId, resultId, studentId]);

  /* ========== MODE B: taking the test now ========== */
  useEffect(() => {
    if (!testId || resultId) return; // skip if viewing a past result
    (async () => {
      setLoading(true);
      try {
        // First, fetch test metadata to check show_cheat_sheet
        const { data: testData, error: testErr } = await supabase
          .from('tests')
          .select('show_cheat_sheet')
          .eq('id', testId)
          .single();
        
        if (testErr) throw testErr;
        const hasCheatSheet = testData?.show_cheat_sheet ?? false;
        setShowCheatSheet(hasCheatSheet);

        // Pull test rows with question + group data (including category_id)
        const { data, error } = await fetchTestQuestionRows(testId);
        if (error) throw error;

        const rows = (data ?? []) as any[];

        // Expand grouped questions exactly once per group_id
        const seenGroups = new Set<string>();
        const expanded: Question[] = [];

        for (const r of rows) {
          const q = r.questions;
          if (!q) continue;

          if (!q.group_id) {
            // Stand-alone question
            expanded.push({
              ...q,
              ordinal: r.ordinal,
              group_id: null,
              group_prompt: null,
              part_label: null,
            });
            continue;
          }

          // Grouped: only expand the first time we see the group
          if (seenGroups.has(q.group_id)) continue;
          seenGroups.add(q.group_id);

          const siblings = rows
            .filter((rr) => rr?.questions?.group_id === q.group_id)
            .sort((a, b) => {
              const ao = a.questions?.ordinal ?? 0;
              const bo = b.questions?.ordinal ?? 0;
              return ao - bo;
            });

          const shared =
            (q.group && ('prompt' in q.group ? q.group.prompt : q.group?.[0]?.prompt)) || '';

          siblings.forEach((rr: any, idx: number) => {
            const qq = rr.questions;
            expanded.push({
              ...qq,
              ordinal: rr.ordinal,
              group_id: qq.group_id,
              group_prompt: shared,                          // attach to every part
              part_label: `Part ${String.fromCharCode(65 + idx)}`, // A, B, C, ...
            });
          });
        }

        setQuestions(expanded);

        // If cheat sheet is enabled, fetch equations for all categories in the test
        if (hasCheatSheet) {
          // Collect unique category IDs from questions
          const categoryIds = Array.from(
            new Set(
              rows
                .map((r) => r.questions?.category_id)
                .filter((id): id is string => typeof id === 'string' && id.length > 0)
            )
          );

          if (categoryIds.length > 0) {
            // Fetch categories with their names
            const { data: categories, error: catErr } = await supabase
              .from('categories')
              .select('id, name')
              .in('id', categoryIds);

            if (!catErr && categories) {
              const categoryMap = new Map(
                categories.map((c: any) => [c.id, c.name])
              );

              // Fetch equations for these categories
              const { data: equations, error: eqErr } = await supabase
                .from('equations')
                .select('id, equation, category_id')
                .in('category_id', categoryIds);

              if (!eqErr && equations) {
                // Map equations with category names
                const equationsWithCategories = equations.map((eq: any) => ({
                  id: eq.id,
                  category_id: eq.category_id,
                  category_name: categoryMap.get(eq.category_id) || 'Unknown Category',
                  equation: eq.equation,
                }));

                setCheatSheetEquations(equationsWithCategories);
              }
            }
          }
        }

        // Restore saved progress if available
        if (testId) {
          const saved = loadProgress(testId, expanded.length);
          if (saved) {
            setStep(saved.step);
            setResults(saved.results);
            setScore(saved.score);
            setSelected(saved.selected);
            setTyped(saved.typed ?? '');
            stashedAnswersRef.current = saved.stashedAnswers ?? {};
          }
        }

        setError(null);
      } catch (e: any) {
        setError(e.message || 'Failed to load test.');
      } finally {
        setLoading(false);
      }
    })();
  }, [testId, resultId]);

  const totalMarks = () =>
    phase === 'review' && results.length
      ? results.reduce((s, r) => s + (r.maxMarks || 0), 0)
      : questions.reduce((s, q) => s + (q.marks || 0), 0);

  // Build a result row for the CURRENT question (without advancing UI state)
  const buildCurrentResult = (): { row: QuestionResult; earned: number } => {
    const q = questions[step];
    const choices = asChoiceArray(q.choices);
    const submittable = toSubmittableQuestion(q);
    const markingType = resolveMarkingType(submittable);
    const isManualInput = markingType === 'manual';
    const isAiInput = isAiMarkingType(markingType);
    const markOutcome = clientScoresMarkingType(markingType)
      ? scoreQuestionSubmission(submittable, {
          finalAnswer: q.type === 'mc' ? null : typed.trim(),
          selectedIndex: q.type === 'mc' ? selected ?? null : null,
        })
      : null;
    const earned = clientScoresMarkingType(markingType) ? (markOutcome?.totalAwarded ?? 0) : 0;

    const now = Date.now();
    const secs = Math.round((now - startRef.current) / 1000);

    const row: QuestionResult = {
      questionId: q.id,
      type: q.type,
      prompt: q.prompt,
      choices: q.type === 'mc' ? choices : undefined,
      mcChoicesRaw: q.type === 'mc' ? unwrapChoicesJson(q.choices) : undefined,
      correctIndex: q.type === 'mc' ? (q.answer_index ?? undefined) : undefined,
      selectedIndex: q.type === 'mc' ? selected! : undefined,
      yourAnswer: q.type === 'mc' ? (selected != null ? choices[selected] ?? '' : '') : typed.trim(),
      correctAnswer:
        q.type === 'mc'
          ? (q.answer_index != null ? choices[q.answer_index] ?? '' : '')
          : (q.answer ?? ''),
      workedSolution: (q as any).worked_solution ?? null,
      score: earned,
      maxMarks: q.marks || 0,
      timeTaken: secs,
      markingStatus: isManualInput || isAiInput || markOutcome?.requiresReview
        ? 'pending_manual'
        : 'auto_marked',
      tutorAwardedMarks: null,
      tutorFeedback: null,
      responseFormat: isManualInput || isAiInput ? 'richtext' : 'mathlive',
      selfMarked: false,
    };

    return { row, earned };
  };

  // Go to previous (pop last recorded result and restore UI)
  const handlePrev = () => {
    if (step === 0 || phase !== 'doing') return;
    const last = results[results.length - 1];
    if (last) {
      // Stash the current question's unsaved answer so it can be restored on Next
      stashedAnswersRef.current[step] = { selected, typed };

      const nextResults = results.slice(0, results.length - 1);
      const nextScore = score - last.score;
      const nextStep = step - 1;
      const prevQ = questions[nextStep];
      const nextSelected = prevQ.type === 'mc' ? (last.selectedIndex ?? undefined) : undefined;
      const nextTyped = prevQ.type === 'mc' ? '' : (last.yourAnswer ?? '');

      setResults(nextResults);
      setScore(nextScore);
      setSelected(nextSelected);
      setTyped(nextTyped);
      setStep(nextStep);
      startRef.current = Date.now();

      if (testId) {
        saveProgress(testId, {
          step: nextStep,
          results: nextResults,
          score: nextScore,
          selected: nextSelected,
          typed: nextTyped,
          questionCount: questions.length,
          stashedAnswers: { ...stashedAnswersRef.current },
        });
      }
    } else {
      setStep((s) => s - 1);
      startRef.current = Date.now();
    }
  };

  // Normal next (record & advance) — only used when NOT on last question
  const handleNext = () => {
    if (phase !== 'doing') return;
    const isLast = step === questions.length - 1;
    if (isLast) return;

    const { row, earned } = buildCurrentResult();
    const nextResults = [...results, row];
    const nextScore = score + earned;
    const nextStep = step + 1;

    // Restore stashed answer for the next question if user previously navigated back
    const stashed = stashedAnswersRef.current[nextStep];
    const nextSelected = stashed?.selected;
    const nextTyped = stashed?.typed ?? '';
    if (stashed) delete stashedAnswersRef.current[nextStep];

    setResults(nextResults);
    setScore(nextScore);
    setSelected(nextSelected);
    setTyped(nextTyped);
    startRef.current = Date.now();
    setStep(nextStep);

    if (testId) {
      saveProgress(testId, {
        step: nextStep,
        results: nextResults,
        score: nextScore,
        selected: nextSelected,
        typed: nextTyped,
        questionCount: questions.length,
        stashedAnswers: { ...stashedAnswersRef.current },
      });
    }
  };

  // Re-mark canonical-answer input questions that the deterministic engine scored
  // below full marks, using the AI marker as adjudicator. Returns a map of
  // questionId -> patched result fields (only for questions the AI changed/scored).
  const adjudicateWrongInputsWithAi = async (
    rows: QuestionResult[],
    insertedRows: Array<{ id: string; question_id: string }>,
  ): Promise<Map<string, Partial<QuestionResult>>> => {
    const updates = new Map<string, Partial<QuestionResult>>();
    try {
      const idByQuestion = new Map<string, string>();
      for (const ir of insertedRows) idByQuestion.set(String(ir.question_id), String(ir.id));

      const candidates = rows.filter(
        (r) =>
          r.type === 'input' &&
          r.markingStatus === 'auto_marked' &&
          typeof r.correctAnswer === 'string' &&
          r.correctAnswer.trim() !== '' &&
          typeof r.yourAnswer === 'string' &&
          r.yourAnswer.trim() !== '' &&
          r.score < r.maxMarks,
      );
      if (!candidates.length) return updates;

      const adjudications = await Promise.all(
        candidates.map(async (r) => {
          const ai = await invokeShadowMarkAiSubmission(
            {
              questionId: r.questionId,
              studentAnswer: r.yourAnswer,
              questionResultId: idByQuestion.get(r.questionId),
            },
            { awaitResult: true },
          );
          return { result: r, ai };
        }),
      );

      for (const { result: r, ai } of adjudications) {
        if (!ai) continue;
        const newScore = ai.awarded > r.score ? Math.min(ai.awarded, r.maxMarks) : r.score;
        updates.set(r.questionId, {
          score: newScore,
          aiFeedback: ai.feedback ?? null,
          aiAdjudicated: true,
        });
      }
    } catch (err) {
      console.warn('AI adjudication (test) failed:', err);
    }
    return updates;
  };

  // Submit attempt (called from LAST question)
  const handleSubmitFromLast = async () => {
    if (phase !== 'doing') return;

    const { row, earned } = buildCurrentResult();
    let finalResults = [...results, row];
    const hasPendingManual = finalResults.some((r) => r.markingStatus === 'pending_manual');
    let provisionalScore = score + earned;

    setSubmitting(true);
    try {
      const { data: { user } = { user: null } } = await supabase.auth.getUser();
      if (!user) throw new Error('Login first');

      if (!studentId) {
        throw new Error('Student record not found.');
      }

      let ins: any = null;
      let err1: any = null;
      const insertWithMarking = await markingDb
        .from('test_results')
        .insert({
          test_id: testId,
          student_id: studentId,
          score: provisionalScore,
          total_marks: totalMarks(),
          finished_at: new Date().toISOString(),
          ...(hasMarkingColumns ? { marking_status: hasPendingManual ? 'pending_manual' : 'final' } : {}),
        })
        .select('id')
        .single();
      ins = insertWithMarking.data;
      err1 = insertWithMarking.error;
      if (
        err1 &&
        typeof err1.message === 'string' &&
        (err1.message.includes('marking_status') || err1.message.includes('schema cache'))
      ) {
        const legacyInsert = await markingDb
          .from('test_results')
          .insert({
            test_id: testId,
            student_id: studentId,
            score: provisionalScore,
            total_marks: totalMarks(),
            finished_at: new Date().toISOString(),
          })
          .select('id')
          .single();
        ins = legacyInsert.data;
        err1 = legacyInsert.error;
        if (!err1) setHasMarkingColumns(false);
      }

      if (err1 || !ins?.id) throw new Error(err1?.message || 'Insert failed');

      const rows = finalResults.map((r: any) => ({
        result_id: ins.id,
        question_id: r.questionId,
        time_taken: `${r.timeTaken} seconds`,
        earned_marks: r.score,
        answered_at: new Date().toISOString(),
        your_answer: r.yourAnswer ?? null,
        selected_index: r.selectedIndex ?? null,
        ...(hasMarkingColumns
          ? {
              marking_status: r.markingStatus,
              tutor_awarded_marks: null,
              tutor_feedback: null,
            }
          : {}),
      }));

      let err2: any = null;
      let insertedRows: Array<{ id: string; question_id: string }> = [];
      const insertRows = await markingDb
        .from('question_results')
        .insert(rows)
        .select('id, question_id');
      err2 = insertRows.error;
      insertedRows = (insertRows.data as Array<{ id: string; question_id: string }>) ?? [];
      if (
        err2 &&
        typeof err2.message === 'string' &&
        (err2.message.includes('marking_status') ||
          err2.message.includes('tutor_awarded_marks') ||
          err2.message.includes('schema cache'))
      ) {
        const legacyRows = finalResults.map((r) => ({
          result_id: ins.id,
          question_id: r.questionId,
          time_taken: `${r.timeTaken} seconds`,
          earned_marks: r.score,
          answered_at: new Date().toISOString(),
          your_answer: r.yourAnswer ?? null,
          selected_index: r.selectedIndex ?? null,
        }));
        const legacyInsertRows = await markingDb
          .from('question_results')
          .insert(legacyRows)
          .select('id, question_id');
        err2 = legacyInsertRows.error;
        insertedRows = (legacyInsertRows.data as Array<{ id: string; question_id: string }>) ?? [];
        if (!err2) setHasMarkingColumns(false);
      }
      if (err2) throw new Error(err2.message);

      invokeShadowMarkTestAttempt(String(ins.id));

      const aiUpdates = await adjudicateWrongInputsWithAi(finalResults, insertedRows);
      if (aiUpdates.size) {
        finalResults = finalResults.map((r) => {
          const upd = aiUpdates.get(r.questionId);
          return upd ? { ...r, ...upd } : r;
        });
        provisionalScore = finalResults.reduce(
          (sum, r) => sum + (Number.isFinite(r.score) ? r.score : 0),
          0,
        );
      }

      if (tutorSession?.id) {
        for (const r of finalResults) {
          const hasAssignedMark = r.markingStatus !== 'pending_manual';
          await logTutorSessionEvent(tutorSession.id, 'question_attempt', {
            mode: 'test',
            test_id: testId,
            question_id: r.questionId,
            correct: hasAssignedMark ? r.score === r.maxMarks : null,
            earned_marks: hasAssignedMark ? r.score : null,
            max_marks: r.maxMarks,
            time_taken: r.timeTaken,
            marking_status: r.markingStatus,
            selected_index: r.selectedIndex ?? null,
            your_answer: r.yourAnswer || null,
            answered: hasAssignedMark,
          });
        }
      }

      // Success → show review
      if (testId) clearProgress(testId);
      stashedAnswersRef.current = {};
      setResults(finalResults);
      setScore(provisionalScore);
      setSubmittedResultId(String(ins.id));
      setResultMarkingStatus(hasPendingManual ? 'pending_manual' : 'final');
      setPhase('review');
      setStep(finalResults.length);
    } catch (e: any) {
      alert(e.message || 'Failed to submit results.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress =
    !questions.length ? 0 : Math.min(100, Math.max(0, (step / questions.length) * 100));

  const manualRows = useMemo(
    () => results.filter((r) => r.markingStatus !== 'auto_marked'),
    [results],
  );
  const selfMarkableRows = useMemo(
    () => results.filter((r) => r.markingStatus === 'pending_manual'),
    [results],
  );

  useEffect(() => {
    if (!isSessionSelfMarkMode || phase !== 'review') return;
    setMarkDrafts((prev) => {
      const next = { ...prev };
      selfMarkableRows.forEach((row) => {
        if (next[row.questionId] == null) next[row.questionId] = '';
      });
      return next;
    });
  }, [isSessionSelfMarkMode, phase, selfMarkableRows]);

  const saveSessionSelfMarks = async () => {
    if (!isSessionSelfMarkMode || !tutorSession?.id) return;
    setSavingSelfMarks(true);
    setMarkingError(null);
    setMarkingMessage(null);
    try {
      const updatedRows = [...results];
      let touchedAny = false;
      for (const row of selfMarkableRows) {
        const raw = (markDrafts[row.questionId] ?? '').trim();
        if (!raw) continue;
        const value = Number(raw);
        if (!Number.isFinite(value) || value < 0 || value > row.maxMarks) {
          throw new Error(`Mark for question ${row.questionId} must be between 0 and ${row.maxMarks}.`);
        }
        const feedback = (feedbackDrafts[row.questionId] ?? '').trim();
        const idx = updatedRows.findIndex((r) => r.questionId === row.questionId);
        if (idx >= 0) {
          updatedRows[idx] = {
            ...updatedRows[idx],
            score: value,
            tutorAwardedMarks: value,
            tutorFeedback: feedback || null,
            markingStatus: 'manually_marked',
            selfMarked: true,
          };
          touchedAny = true;
        }
        if (activeResultId) {
          const { error: updateErr } = await markingDb
            .from('question_results')
            .update({
              earned_marks: value,
              ...(hasMarkingColumns
                ? {
                    tutor_awarded_marks: value,
                    tutor_feedback: feedback || null,
                    marking_status: 'manually_marked',
                    marked_at: new Date().toISOString(),
                  }
                : {}),
            })
            .eq('result_id', activeResultId)
            .eq('question_id', row.questionId);
          if (updateErr) throw new Error(updateErr.message);
        }
        await logTutorSessionEvent(tutorSession.id, 'question_attempt', {
          mode: 'test',
          test_id: testId,
          question_id: row.questionId,
          correct: value === row.maxMarks,
          earned_marks: value,
          max_marks: row.maxMarks,
          marking_status: 'manually_marked',
          self_marked: true,
          self_mark_feedback: feedback || null,
          your_answer: row.yourAnswer || null,
          selected_index: row.selectedIndex ?? null,
          answered: true,
        });
      }
      if (!touchedAny) {
        setMarkingMessage('No self-marks entered.');
        return;
      }
      const nextPending = updatedRows.some((r) => r.markingStatus === 'pending_manual');
      const recomputedScore = updatedRows.reduce((sum, r) => sum + (Number.isFinite(r.score) ? r.score : 0), 0);
      if (activeResultId) {
        const { error: testResultErr } = await markingDb
          .from('test_results')
          .update({
            score: recomputedScore,
            ...(hasMarkingColumns ? { marking_status: nextPending ? 'pending_manual' : 'final' } : {}),
          })
          .eq('id', activeResultId);
        if (testResultErr) throw new Error(testResultErr.message);
      }
      setResults(updatedRows);
      setScore(recomputedScore);
      setResultMarkingStatus(nextPending ? 'pending_manual' : 'final');
      setMarkingMessage(nextPending ? 'Saved self-marks for selected responses.' : 'Self-marking complete.');
    } catch (e: any) {
      setMarkingError(e.message || 'Failed to save self-marks.');
    } finally {
      setSavingSelfMarks(false);
    }
  };

  const saveTutorMarks = async () => {
    if (!resultId || !isTutorMarkingMode) return;
    if (!hasMarkingColumns) {
      setMarkingError('Database migration for marking fields is not applied yet.');
      return;
    }
    setSavingMarks(true);
    setMarkingError(null);
    setMarkingMessage(null);
    try {
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();
      if (userErr || !user) throw new Error('Not authenticated.');

      const nextResults = [...results];
      let touchedAny = false;
      for (const row of manualRows) {
        const raw = (markDrafts[row.questionId] ?? '').trim();
        if (!raw) continue;
        const value = Number(raw);
        if (!Number.isFinite(value) || value < 0 || value > row.maxMarks) {
          throw new Error(`Mark for question ${row.questionId} must be between 0 and ${row.maxMarks}.`);
        }
        const feedback = (feedbackDrafts[row.questionId] ?? '').trim();
        const { error: updateErr } = await markingDb
          .from('question_results')
          .update({
            earned_marks: value,
            tutor_awarded_marks: value,
            tutor_feedback: feedback || null,
            marking_status: 'manually_marked',
            marked_at: new Date().toISOString(),
            marked_by: user.id,
          })
          .eq('result_id', resultId)
          .eq('question_id', row.questionId);
        if (updateErr) throw new Error(updateErr.message);
        touchedAny = true;
        const idx = nextResults.findIndex((r) => r.questionId === row.questionId);
        if (idx >= 0) {
          nextResults[idx] = {
            ...nextResults[idx],
            score: value,
            tutorAwardedMarks: value,
            tutorFeedback: feedback || null,
            markingStatus: 'manually_marked',
          };
        }
      }

      if (!touchedAny) {
        setMarkingMessage('No new marks to save.');
        return;
      }

      const remainingPending = nextResults.some((r) => r.markingStatus === 'pending_manual');
      const recomputedScore = nextResults.reduce((sum, r) => sum + (Number.isFinite(r.score) ? r.score : 0), 0);

      const { error: testResultErr } = await markingDb
        .from('test_results')
        .update({
          score: recomputedScore,
          ...(hasMarkingColumns ? { marking_status: remainingPending ? 'pending_manual' : 'final' } : {}),
        })
        .eq('id', resultId);
      if (testResultErr) throw new Error(testResultErr.message);

      setResults(nextResults);
      setScore(recomputedScore);
      setResultMarkingStatus(remainingPending ? 'pending_manual' : 'final');
      setMarkingMessage(remainingPending ? 'Marks saved. Some questions are still pending.' : 'All marks saved. Test is now final.');
    } catch (e: any) {
      setMarkingError(e.message || 'Failed to save marks.');
    } finally {
      setSavingMarks(false);
    }
  };

  /* ---------------- render ---------------- */

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '40vh' }} spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {resultId ? 'Loading attempt…' : 'Loading test…'}
          </Typography>
        </Stack>
      </Container>
    );
  }
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }
  if (!testId) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography color="error">Missing test ID.</Typography>
      </Container>
    );
  }
  if (isMarkingRoute && !tutorSession?.id) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography color="error">Tutor marking mode is only available in an active tutor session.</Typography>
      </Container>
    );
  }

  /* ---------------- review (after submit OR viewing past attempt) ---------------- */
  if (phase === 'review') {
    return (
      <Container maxWidth="lg" sx={{ py: 3, position: 'relative' }}>
        {/* Back arrow */}
        <Tooltip title="Back">
          <IconButton
            aria-label="Back"
            onClick={() => navigate(-1)}
            size="large"
            disableRipple
            sx={{
              position: 'fixed',
              top: 8,
              left: 8,
              zIndex: 10,
              color: 'grey.600',
              '&:hover': { backgroundColor: 'transparent' },
              '&:hover .MuiSvgIcon-root': { color: 'grey.800' },
            }}
          >
            <KeyboardBackspaceIcon
              fontSize="large"
              sx={{
                fontSize: 45,
                '& path': { stroke: 'currentColor', strokeWidth: 0.6 },
              }}
            />
          </IconButton>
        </Tooltip>

        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {resultMarkingStatus === 'pending_manual' ? 'Provisional score' : 'Your score'}: {score} / {totalMarks()}
          </Typography>
          {resultMarkingStatus === 'pending_manual' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Some written questions are pending tutor marking. Your final score will update after marking is complete.
            </Alert>
          )}
          {isTutorMarkingMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Mark pending written responses below. You can award partial marks from 0 to each question&apos;s max marks.
            </Alert>
          )}
          {isSessionSelfMarkMode && resultMarkingStatus === 'pending_manual' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Self-mark written responses below for session mode. This updates the attempt and session summary.
            </Alert>
          )}
          {!isTutorMarkingMode && tutorSession?.id && activeResultId && resultMarkingStatus === 'pending_manual' && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate(`/tests/${testId}/result/${activeResultId}/mark`)}
              >
                Open Marking Window
              </Button>
            </Stack>
          )}
          {markingError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {markingError}
            </Alert>
          )}
          {markingMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {markingMessage}
            </Alert>
          )}

          <Stack spacing={2} sx={{ maxWidth: 900 }}>
            {results.map((r, i) => {
              const isPendingManual = r.markingStatus === 'pending_manual';
              const isCorrect = !isPendingManual && r.score === r.maxMarks;
              const isTutorMarked = r.markingStatus === 'manually_marked';
              const statusLabel = isPendingManual
                ? 'Pending tutor marking'
                : isTutorMarked
                  ? (r.selfMarked ? 'Self marked' : 'Marked by tutor')
                  : isCorrect
                    ? (r.aiAdjudicated ? 'Correct (AI marked)' : 'Correct')
                    : (r.aiAdjudicated ? 'Incorrect (AI marked)' : 'Incorrect');
              const statusColor = isPendingManual
                ? 'warning.main'
                : isTutorMarked
                  ? 'primary.main'
                  : isCorrect
                    ? 'success.main'
                    : 'error.main';
              const tableReviewMc = getTableChoiceData(r.mcChoicesRaw);
              return (
                <Paper
                  key={r.questionId}
                  variant="outlined"
                  sx={{
                    position: 'relative',
                    p: 2,
                    pt: 4,
                    borderColor: statusColor,
                  }}
                >
                  {/* status bar */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 28,
                      bgcolor: statusColor,
                      color: 'common.white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTopLeftRadius: 1,
                      borderTopRightRadius: 1,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {statusLabel}
                  </Box>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ pb: 1, mb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <Typography variant="subtitle2">Question {i + 1}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={`${r.score} / ${r.maxMarks} pts`}
                        color={isCorrect ? 'success' : isPendingManual ? 'warning' : 'default'}
                        variant={isCorrect || isPendingManual ? 'filled' : 'outlined'}
                      />
                      {!tutorSession?.id && !isTutorMarkingMode && (
                        <QuestionActionButtons
                          questionId={r.questionId}
                          studentId={studentId}
                          companyId={companyId}
                          showSave
                          showFlag={false}
                        />
                      )}
                    </Stack>
                  </Stack>

                  <RichText html={r.prompt} className="question-body" />

                  {r.aiAdjudicated && r.aiFeedback && r.aiFeedback.trim() !== '' && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                      AI feedback: {r.aiFeedback}
                    </Typography>
                  )}

                  {r.type === 'mc' && tableReviewMc ? (
                    <Box sx={{ mt: 1.5 }}>
                      <McChoiceTableStudent
                        readOnly
                        answerMarks="letter-outline"
                        columns={tableReviewMc.columns}
                        rows={tableReviewMc.rows}
                        selectedIndex={r.selectedIndex}
                        onSelect={() => {}}
                        revealAnswers
                        correctIndex={r.correctIndex ?? null}
                        renderCell={(html) => <RichText html={html} />}
                      />
                    </Box>
                  ) : r.choices ? (
                    <Box component="ul" sx={{ listStyle: 'none', pl: 0, mt: 1.5, mb: 0 }}>
                      {r.choices.map((c, idx) => {
                        const ok = idx === r.correctIndex;
                        const sel = idx === r.selectedIndex;
                        const wrongPick = sel && !ok;
                        const optionLabel = optionLetters[idx] ?? String(idx + 1);
                        return (
                          <Box
                            key={idx}
                            component="li"
                            sx={{
                              p: 1,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                border: '2px solid',
                                borderColor: ok ? 'success.main' : wrongPick ? 'error.main' : 'divider',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                              }}
                            >
                              {optionLabel}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                              <RichText html={c} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 1.5 }}>
                      <Box sx={{ minWidth: 160 }}>
                        <Typography variant="caption" color="text.secondary">
                          You answered
                        </Typography>
                        <RichText
                          html={
                            r.yourAnswer
                              ? (r.responseFormat === 'richtext' ? r.yourAnswer : ensureInlineMath(r.yourAnswer))
                              : '—'
                          }
                        />
                      </Box>
                      <Box sx={{ minWidth: 160 }}>
                        <Typography variant="caption" color="text.secondary">
                          {isPendingManual ? 'Marking status' : 'Correct answer'}
                        </Typography>
                        {isPendingManual ? (
                          <Typography variant="body2" color="warning.dark">
                            Waiting for tutor marking
                          </Typography>
                        ) : (
                          <RichText html={r.correctAnswer ? ensureInlineMath(r.correctAnswer) : '—'} />
                        )}
                      </Box>
                    </Stack>
                  )}

                  {isTutorMarkingMode && r.markingStatus !== 'auto_marked' && (
                    <Stack spacing={1.25} sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2">Tutor marking</Typography>
                      <TextField
                        label={`Awarded marks (0 - ${r.maxMarks})`}
                        type="number"
                        size="small"
                        value={markDrafts[r.questionId] ?? ''}
                        onChange={(e) =>
                          setMarkDrafts((prev) => ({
                            ...prev,
                            [r.questionId]: e.target.value,
                          }))
                        }
                        inputProps={{ min: 0, max: r.maxMarks, step: 0.5 }}
                      />
                      <TextField
                        label="Tutor feedback (optional)"
                        size="small"
                        multiline
                        minRows={2}
                        value={feedbackDrafts[r.questionId] ?? ''}
                        onChange={(e) =>
                          setFeedbackDrafts((prev) => ({
                            ...prev,
                            [r.questionId]: e.target.value,
                          }))
                        }
                      />
                    </Stack>
                  )}
                  {isSessionSelfMarkMode && r.markingStatus === 'pending_manual' && (
                    <Stack spacing={1.25} sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2">Self marking</Typography>
                      <TextField
                        label={`Awarded marks (0 - ${r.maxMarks})`}
                        type="number"
                        size="small"
                        value={markDrafts[r.questionId] ?? ''}
                        onChange={(e) =>
                          setMarkDrafts((prev) => ({
                            ...prev,
                            [r.questionId]: e.target.value,
                          }))
                        }
                        inputProps={{ min: 0, max: r.maxMarks, step: 0.5 }}
                      />
                      <TextField
                        label="Feedback (optional)"
                        size="small"
                        multiline
                        minRows={2}
                        value={feedbackDrafts[r.questionId] ?? ''}
                        onChange={(e) =>
                          setFeedbackDrafts((prev) => ({
                            ...prev,
                            [r.questionId]: e.target.value,
                          }))
                        }
                      />
                    </Stack>
                  )}

                  {r.workedSolution && (
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Worked solution
                      </Typography>
                      <RichText html={r.workedSolution} />
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
          {isTutorMarkingMode && manualRows.length > 0 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="contained" onClick={saveTutorMarks} disabled={savingMarks}>
                {savingMarks ? 'Saving marks…' : 'Save Marks'}
              </Button>
            </Stack>
          )}
          {isSessionSelfMarkMode && selfMarkableRows.length > 0 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="contained" onClick={saveSessionSelfMarks} disabled={savingSelfMarks}>
                {savingSelfMarks ? 'Saving…' : 'Save Self Marks'}
              </Button>
            </Stack>
          )}
        </Box>
      </Container>
    );
  }

  /* ---------------- during test ---------------- */
  const isLast = step === questions.length - 1;
  const currentIsManualWritten = q.type === 'input' && isManualInputQuestion(q);
  const hasCurrentAnswer =
    q.type === 'mc'
      ? selected !== undefined
      : currentIsManualWritten
        ? stripHtml(typed).length > 0
        : typed.trim() !== '';

  return (
    <Container maxWidth="lg" sx={{ py: 3, position: 'relative' }}>
      {/* Back arrow with confirm */}
      <Tooltip title="Back">
        <IconButton
          aria-label="Back"
          onClick={() => setShowConfirm(true)}
          size="large"
          disableRipple
          sx={{
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: 10,
            color: 'grey.600',
            '&:hover': { backgroundColor: 'transparent' },
            '&:hover .MuiSvgIcon-root': { color: 'grey.800' },
          }}
        >
          <KeyboardBackspaceIcon
            fontSize="large"
            sx={{
              fontSize: 45,
              '& path': { stroke: 'currentColor', strokeWidth: 0.6 },
            }}
          />
        </IconButton>
      </Tooltip>

      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
        <DialogTitle>Leave test?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Are you sure you want to leave? Your saved progress for this test will be discarded.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)}>No</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (testId) clearProgress(testId);
              stashedAnswersRef.current = {};
              navigate(-1);
            }}
          >
            Yes, leave
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ maxWidth: '70vw', mx: 'auto' }}>
        {/* MUI progress bar */}
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 999 }}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              Question {step + 1} / {questions.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round(progress)}%
            </Typography>
          </Stack>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {/* Show shared group prompt (for every part) */}
          {q.group_prompt && (
            <Box
              sx={{
                p: 1.25,
                mb: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="overline" sx={{ letterSpacing: 0.6 }}>
                Shared prompt
              </Typography>
              <RichText html={q.group_prompt} className="question-body" />
            </Box>
          )}

          {/* Part label if present */}
          {q.part_label && (
            <Typography variant="overline" sx={{ display: 'block', mb: 0.5 }}>
              {q.part_label}
            </Typography>
          )}

          {/* Part-specific prompt */}
          <RichText html={q.prompt} className="question-body" />

          {q.type === 'mc' ? (
            <QuestionMC question={q as any} selected={selected} onSelect={setSelected} />
          ) : (
            <QuestionInput
              question={q as any}
              value={typed}
              onChange={setTyped}
              unit={(q.unit ?? '').trim() || undefined}
              useRichTextEditor={currentIsManualWritten}
            />
          )}
        </Paper>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button variant="outlined" onClick={handlePrev} disabled={step === 0}>
            Back
          </Button>

          <Stack direction="row" spacing={2}>
            {showCheatSheet && (
              <Button
                variant="outlined"
                onClick={() => setShowCheatSheetDialog(true)}
                sx={{ minWidth: 150 }}
              >
                Show Cheat Sheet
              </Button>
            )}
            <QuestionActionButtons
              questionId={q?.id}
              studentId={studentId}
              companyId={companyId}
              showSave={false}
              showFlag
            />

            <Button
              variant="contained"
              size="large"
              onClick={isLast ? handleSubmitFromLast : handleNext}
              disabled={!hasCurrentAnswer}
            >
              {isLast ? 'Submit' : 'Next'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Cheat Sheet Dialog */}
      <Dialog
        open={showCheatSheetDialog}
        onClose={() => setShowCheatSheetDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Cheat Sheet</DialogTitle>
        <DialogContent dividers>
          {cheatSheetEquations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No equations available for this test.
            </Typography>
          ) : (
            <Stack spacing={3}>
              {Array.from(
                new Set(cheatSheetEquations.map((eq) => eq.category_id))
              ).map((categoryId) => {
                const categoryName = cheatSheetEquations.find(
                  (eq) => eq.category_id === categoryId
                )?.category_name || 'Unknown Category';
                const categoryEquations = cheatSheetEquations.filter(
                  (eq) => eq.category_id === categoryId
                );

                return (
                  <Box key={categoryId}>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                      {categoryName}
                    </Typography>
                    <Stack spacing={1.5}>
                      {categoryEquations.map((eq) => (
                        <Paper
                          key={eq.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderColor: 'divider',
                          }}
                        >
                          <RichText html={eq.equation} />
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheatSheetDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Submitting overlay */}
      <Backdrop open={submitting} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" color="common.white">
            Submitting…
          </Typography>
        </Stack>
      </Backdrop>
    </Container>
  );
};

export default TestPage;
